
export class FormSelect extends HTMLElement {
    static get observedAttributes() {
        return ['label', 'name', 'value', 'options', 'disabled'];
    }

    private innerSelect: HTMLSelectElement | null = null;
    private _options: { value: string, label: string }[] = [];

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        this.render();
        this.innerSelect = this.shadowRoot?.querySelector('select') || null;
        this.setupEventListeners();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            if (name === 'options') {
                try {
                    this._options = JSON.parse(newValue);
                    this.render();
                } catch (e) {
                    this._options = [];
                }
            } else if (name === 'value' && this.innerSelect) {
                this.innerSelect.value = newValue;
            } else {
                this.render();
            }
        }
    }

    private setupEventListeners() {
        if (!this.innerSelect) return;

        this.innerSelect.addEventListener('change', (e) => {
            e.stopPropagation(); // Prevent native change event from bubbling
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                composed: true,
                detail: { value: (e.target as HTMLSelectElement).value, name: this.getAttribute('name') }
            }));
        });
    }

    get value() {
        return this.innerSelect?.value || '';
    }

    set value(val: string) {
        if (this.innerSelect) {
            this.innerSelect.value = val;
        }
        this.setAttribute('value', val);
    }

    get options() {
        return this._options;
    }

    set options(val: { value: string, label: string }[]) {
        this._options = val;
        this.render();
    }

    render() {
        const label = this.getAttribute('label') || '';
        const name = this.getAttribute('name') || '';
        const value = this.getAttribute('value') || '';
        const disabled = this.hasAttribute('disabled');

        if (this.shadowRoot) {
            this.shadowRoot.innerHTML = `
                <style>
                    :host {
                        display: block;
                        margin-bottom: 1.5rem;
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .form-group {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }
                    label {
                        color: var(--accent, #f59e0b);
                        font-size: 0.7rem;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.15em;
                        opacity: 0.8;
                    }
                    select {
                        width: 100%;
                        padding: 0.75rem;
                        background: rgba(255, 255, 255, 0.05);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 8px;
                        color: white;
                        font-size: 0.9rem;
                        font-family: inherit;
                        box-sizing: border-box;
                        cursor: pointer;
                    }
                    select:focus {
                        outline: none;
                        border-color: var(--accent, #f59e0b);
                        box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
                    }
                    select option {
                        background: #1e293b !important;
                        color: white !important;
                    }
                    select:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                </style>
                <div class="form-group">
                    ${label ? `<label for="${name}">${label}</label>` : ''}
                    <select id="${name}" name="${name}" ${disabled ? 'disabled' : ''}>
                        ${this._options.map(opt => `
                            <option value="${opt.value}" ${opt.value === value ? 'selected' : ''}>${opt.label}</option>
                        `).join('')}
                    </select>
                </div>
            `;

            this.innerSelect = this.shadowRoot.querySelector('select');
            this.setupEventListeners();
        }
    }
}

customElements.define('form-select', FormSelect);
